package com.elan.edubridge.data.firebase

/**
 * Firebase Auth 에러 코드(`auth/` 접두사)를 사용자가 해결할 수 있는 한국어 문장으로 변환한다
 * (MVP §9:364 "공급자 코드가 아닌 사용자가 해결할 수 있는 문장").
 *
 * 코드는 `"auth/wrong-password"`처럼 접두사가 붙어 오므로 마지막 세그먼트만 비교한다.
 * `wrong-password`/`user-not-found`/`invalid-credential` 세 코드를 모두 매핑해 둔 이유:
 * firebase@12.17.0 타입 선언(auth.d.ts)에 세 값이 모두 남아 있고, 이메일 유출 방지를 위해
 * `signInWithEmailAndPassword`가 통합 코드(`invalid-credential`)를 돌려주도록 바뀐 백엔드도 있어
 * 에뮬레이터·프로덕션 중 어느 쪽이 응답해도 같은 문장을 보여줘야 한다.
 */
object AuthErrorMapper {
    fun toKoreanMessage(code: String?): String {
        val normalized = code?.substringAfterLast('/') ?: return DEFAULT_MESSAGE
        return MESSAGES[normalized] ?: DEFAULT_MESSAGE
    }

    private const val DEFAULT_MESSAGE = "요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요."

    private val MESSAGES = mapOf(
        "wrong-password" to "이메일 또는 비밀번호를 확인해 주세요.",
        "invalid-credential" to "이메일 또는 비밀번호를 확인해 주세요.",
        "invalid-login-credentials" to "이메일 또는 비밀번호를 확인해 주세요.",
        "user-not-found" to "이메일 또는 비밀번호를 확인해 주세요.",
        "invalid-email" to "올바른 이메일 형식이 아니에요.",
        "email-already-in-use" to "이미 가입된 이메일이에요.",
        "weak-password" to "비밀번호는 6자 이상이어야 해요.",
        "too-many-requests" to "너무 많이 시도했어요. 잠시 후 다시 시도해 주세요.",
        "network-request-failed" to "네트워크 연결을 확인해 주세요.",
        "user-disabled" to "비활성화된 계정이에요. 고객센터에 문의해 주세요.",
        "operation-not-allowed" to "지금은 이 방식으로 로그인할 수 없어요.",
        "requires-recent-login" to "보안을 위해 다시 로그인해 주세요.",
        "user-token-expired" to "세션이 만료되었어요. 다시 로그인해 주세요.",
        // Google 팝업 로그인(FirebaseAccountDataSource.signInWithGoogle()) 전용. popup-closed-by-user/
        // cancelled-popup-request는 매핑은 갖되 호출부가 "취소"로 판단해 조용히 무시하고 이 문구를 띄우지
        // 않는다 — popup-blocked만 실제로 사용자에게 보여지는 실질 오류다.
        "popup-blocked" to "팝업이 차단됐어요. 브라우저에서 팝업을 허용한 뒤 다시 시도해 주세요.",
        "popup-closed-by-user" to "로그인이 취소됐어요.",
        "cancelled-popup-request" to "로그인이 취소됐어요.",
    )
}
